import { useState } from 'react';
import type { ReactNode } from 'react';
import { App } from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import { Badge, Button, Dialog } from '../../../components/ui';
import { useT } from '../../../i18n';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { cn } from '../../../lib/cn';
import { LOAI_VAN_BAN, NHAN_NGUON, NHAN_SU_KIEN, NHAN_XAC_THUC_WEBHOOK, laNguonBiet } from '../constants';
import { useGuiThuWebhook, useNhatKyWebhook } from '../hooks/useVanBan';
import type { SuKienWebhook, TichHop } from '../types';

/** Đường tiếp nhận của module dưới Gateway; mở thật ở GN-D2-6. */
const DUONG_TIEP_NHAN = '/api/v1/m/van-ban/inbound';

function Phan({ tieuDe, children }: { tieuDe: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-default bg-subtle p-3.5">
      <h3 className="text-2xs font-semibold uppercase tracking-wide text-content-secondary">{tieuDe}</h3>
      {children}
    </section>
  );
}

function O({ nhan, giaTri, tong }: { nhan: string; giaTri: string; tong?: 'success' | 'muted' }) {
  return (
    <div className="min-w-0 rounded border border-default bg-surface px-3 py-2">
      <div className="mb-0.5 text-2xs uppercase tracking-wide text-content-muted">{nhan}</div>
      <div
        className={cn(
          'break-words text-sm font-medium',
          tong === 'success' ? 'text-success-700' : tong === 'muted' ? 'text-content-muted' : 'text-content-primary',
        )}
      >
        {giaTri}
      </div>
    </div>
  );
}

/** Hộp thoại "Quản lý tích hợp" của bản cũ: API key, cách kết nối, webhook, gửi thử, nhật ký. */
export function QuanLyTichHopDialog({
  tichHop,
  coTheSua,
  coTheXoa,
  coTheCapLai,
  dangCapLai,
  onClose,
  onSua,
  onXoa,
  onCapLai,
  onSaoChep,
}: {
  tichHop: TichHop | null;
  coTheSua: boolean;
  coTheXoa: boolean;
  coTheCapLai: boolean;
  dangCapLai: boolean;
  onClose: () => void;
  onSua: (r: TichHop) => void;
  onXoa: (r: TichHop) => void;
  onCapLai: (r: TichHop) => void;
  onSaoChep: (chu: string, khoaThanhCong: 'vanBanTichHop.daSaoChepLenh') => void;
}) {
  const { t, locale } = useT();
  const { modal } = App.useApp();
  const guiThu = useGuiThuWebhook();
  const [xemNhatKy, setXemNhatKy] = useState(false);
  const nhatKy = useNhatKyWebhook(tichHop?.id ?? null, xemNhatKy);
  if (!tichHop) return null;
  const r = tichHop;

  const nhanNguon = laNguonBiet(r.source_label) ? t(NHAN_NGUON[r.source_label]) : r.source_label;
  const endpoint = `${window.location.origin}${DUONG_TIEP_NHAN}`;
  const header = r.inbound_auth_type === 'BEARER' ? 'Authorization: Bearer <API_KEY>' : 'X-Api-Key: <API_KEY>';
  const loaiVb = r.default_doc_type || LOAI_VAN_BAN[0]!;
  const lenh =
    r.inbound_format === 'JSON'
      ? [
          `curl -X POST '${endpoint}' \\`,
          `  -H '${header}' \\`,
          `  -H 'Content-Type: application/json' \\`,
          `  -d '{`,
          `    "title": "…",`,
          `    "doc_type": "${loaiVb}",`,
          `    "doc_id": "EXT-001",`,
          `    "content": "…"`,
          `  }'`,
        ].join('\n')
      : [
          `curl -X POST '${endpoint}' \\`,
          `  -H '${header}' \\`,
          `  -F 'title=…' \\`,
          `  -F 'doc_type=${loaiVb}' \\`,
          `  -F 'doc_id=EXT-001' \\`,
          `  -F 'file=@document.pdf'`,
        ].join('\n');

  const hoiCapLai = () =>
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t('vanBanQl.xacNhanCapLai', { ten: r.name }),
      content: t('vanBanQl.xacNhanCapLaiMo'),
      okText: t('vanBanQl.nutCapLai'),
      okButtonProps: { danger: true },
      cancelText: t('chung.huy'),
      onOk: () => onCapLai(r),
    });

  const kq = guiThu.data && guiThu.variables === r.id ? guiThu.data : null;

  return (
    <Dialog
      open
      onClose={onClose}
      width={760}
      destroyOnHidden
      title={t('vanBanQl.tieuDe', { ten: r.name })}
      footer={
        <div className="flex flex-wrap justify-between gap-3">
          {coTheXoa ? (
            <Button variant="outline" danger icon={<DeleteOutlined />} onClick={() => onXoa(r)}>
              {t('vanBanTichHop.xoa')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            {coTheSua && (
              <Button variant="outline" icon={<EditOutlined />} onClick={() => onSua(r)}>
                {t('vanBanTichHop.sua')}
              </Button>
            )}
            <Button variant="primary" onClick={onClose}>
              {t('vanBanChung.dong')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <O nhan={t('vanBanQl.iNguon')} giaTri={nhanNguon} />
          <O nhan={t('vanBanQl.iLoaiVb')} giaTri={r.default_doc_type || LOAI_VAN_BAN[LOAI_VAN_BAN.length - 1]!} />
          <O nhan={t('vanBanQl.iTuDong')} giaTri={t(r.auto_summarize ? 'vanBanQl.co' : 'vanBanQl.khong')} />
          <O
            nhan={t('vanBanQl.iTrangThai')}
            giaTri={t(r.is_active ? 'vanBanTichHop.dangHoatDong' : 'vanBanTichHop.daTat')}
            tong={r.is_active ? 'success' : 'muted'}
          />
        </div>

        <Phan tieuDe={t('vanBanQl.phanKhoa')}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 font-mono text-sm text-content-primary">
              <KeyOutlined className="text-content-muted" />
              {r.api_key_prefix}
              <span className="text-content-muted">••••••••••••••••</span>
            </div>
            {coTheCapLai && (
              <Button variant="outline" size="sm" icon={<ReloadOutlined />} loading={dangCapLai} onClick={hoiCapLai}>
                {t('vanBanQl.capLai')}
              </Button>
            )}
          </div>
          <p className="text-xs text-content-muted">{t('vanBanQl.khoaMo')}</p>
        </Phan>

        <Phan tieuDe={t('vanBanQl.phanKetNoi')}>
          <p className="flex items-start gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-700">
            <InfoCircleOutlined className="mt-0.5 shrink-0" />
            {t('vanBanQl.chuaMoInbound')}
          </p>
          <p className="text-xs text-content-muted">{t('vanBanQl.ketNoiMo', { nguon: nhanNguon })}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <O nhan={t('vanBanQl.iEndpoint')} giaTri={`POST ${endpoint}`} />
            <O nhan={t('vanBanQl.iDinhDang')} giaTri={t(`vanBanQl.dinhDangDai.${r.inbound_format}`)} />
            <O nhan={t('vanBanQl.iHeader')} giaTri={header} />
            <O nhan={t('vanBanQl.iTuDong')} giaTri={t(r.auto_summarize ? 'vanBanQl.co' : 'vanBanQl.khong')} />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-content-secondary">{t('vanBanQl.viDu')}</span>
              <Button
                variant="ghost"
                size="sm"
                icon={<CopyOutlined />}
                onClick={() => onSaoChep(lenh, 'vanBanTichHop.daSaoChepLenh')}
              >
                {t('vanBanKhoa.saoChep')}
              </Button>
            </div>
            <pre className="overflow-x-auto rounded border border-default bg-surface px-3.5 py-2.5 font-mono text-xs leading-relaxed text-content-primary">
              {lenh}
            </pre>
          </div>
          <p className="text-xs text-content-muted">{t('vanBanQl.truongMo')}</p>
        </Phan>

        <Phan tieuDe={t('vanBanTichHopForm.phanWebhook')}>
          {r.webhook_url ? (
            <>
              <div className="space-y-1 text-sm">
                <div className="break-all">
                  <span className="text-content-muted">{t('vanBanQl.iUrl')} </span>
                  <span className="font-mono text-content-primary">
                    {r.webhook_method} {r.webhook_url}
                  </span>
                </div>
                <div>
                  <span className="text-content-muted">{t('vanBanQl.iXacThuc')} </span>
                  {t('vanBanQl.thuLaiToiDa', { xacThuc: t(NHAN_XAC_THUC_WEBHOOK[r.webhook_auth_type]), so: r.webhook_retry_max })}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-content-muted">{t('vanBanQl.kichHoatKhi')}</span>
                  {r.webhook_triggers.length ? (
                    r.webhook_triggers.map((s: SuKienWebhook) => (
                      <Badge key={s} variant="primary">
                        {NHAN_SU_KIEN[s] ? t(NHAN_SU_KIEN[s]) : s}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-content-muted">{t('chung.khongCo')}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {coTheSua && (
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<ThunderboltOutlined />}
                    loading={guiThu.isPending}
                    onClick={() => guiThu.mutate(r.id)}
                  >
                    {t('vanBanQl.guiThu')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  icon={<ApiOutlined />}
                  loading={nhatKy.isFetching}
                  onClick={() => (xemNhatKy ? void nhatKy.refetch() : setXemNhatKy(true))}
                >
                  {t('vanBanQl.xemNhatKy')}
                </Button>
              </div>
              {kq && (
                <div
                  role="status"
                  className={cn(
                    'flex items-start gap-1.5 rounded border px-3 py-2 text-xs',
                    kq.success
                      ? 'border-success-200 bg-success-50 text-success-700'
                      : 'border-danger-200 bg-danger-50 text-danger-700',
                  )}
                >
                  {kq.success ? <CheckCircleOutlined className="mt-0.5" /> : <CloseCircleOutlined className="mt-0.5" />}
                  <span>
                    {kq.success
                      ? t('vanBanQl.guiThanhCong', { ma: kq.http_status ?? '' })
                      : t('vanBanQl.guiThatBai', { loi: kq.error ?? `HTTP ${kq.http_status}` })}
                  </span>
                </div>
              )}
              {xemNhatKy && nhatKy.data && (
                nhatKy.data.data.length === 0 ? (
                  <p className="pt-1 text-xs text-content-muted">{t('vanBanQl.nhatKyRong')}</p>
                ) : (
                  <div className="overflow-x-auto rounded border border-default">
                    <table className="w-full text-xs">
                      <caption className="sr-only">{t('vanBanQl.nkCaption')}</caption>
                      <thead className="bg-surface text-content-muted">
                        <tr>
                          <th scope="col" className="px-3 py-2 text-left font-medium">{t('vanBanQl.nkSuKien')}</th>
                          <th scope="col" className="px-3 py-2 text-left font-medium">{t('vanBanQl.nkLan')}</th>
                          <th scope="col" className="px-3 py-2 text-left font-medium">{t('vanBanQl.nkKetQua')}</th>
                          <th scope="col" className="px-3 py-2 text-left font-medium">{t('vanBanQl.nkThoiGian')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-default bg-surface">
                        {nhatKy.data.data.map((l) => (
                          <tr key={l.id}>
                            <td className="px-3 py-1.5 text-content-secondary">{l.trigger}</td>
                            <td className="px-3 py-1.5 tabular-nums text-content-secondary">{l.attempt}</td>
                            <td className="px-3 py-1.5">
                              <span className={l.status === 'SUCCESS' ? 'text-success-700' : 'text-danger-700'}>
                                {l.status}
                                {l.http_status ? ` (${l.http_status})` : ''}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 tabular-nums text-content-muted">{ngayGioVN(l.created_at, locale)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          ) : (
            <p className="text-sm text-content-muted">{t('vanBanQl.chuaCoWebhook')}</p>
          )}
        </Phan>
      </div>
    </Dialog>
  );
}
